import { DateTime } from 'luxon';

/**
 * Utility class to manage animation queue for day/slot transitions.
 * Prevents animation interruption when rapidly clicking dates.
 *
 * INVARIANT: complete() MUST run before the date-handler returns on every
 * exit path. If complete() is skipped (uncaught throw, viewDestroyed
 * early-return, etc.), _isAnimating stays true and every subsequent date
 * click is silently dropped by tryStart(). viewDestroyed is per-instance
 * and is reset to false on the next ionViewWillEnter, so a re-entered
 * page on the SAME instance would inherit the stuck queue (a fresh page
 * instance after destroy+recreate gets a fresh queue and is unaffected).
 *
 * Two safe patterns:
 *
 * 1) When completion is driven later, by a timer or animation-end callback:
 * ```
 * async onCalendarDateSelected(date: DateTime) {
 *   if (!this.dayQueue.tryStart(date)) return;
 *   let handedOffToAnimation = false;
 *   try {
 *     // ...async work, may early-return on viewDestroyed...
 *     this.releaseDayQueueAfterSlide();
 *     handedOffToAnimation = true;
 *   } finally {
 *     if (!handedOffToAnimation) this.dayQueue.complete();
 *   }
 * }
 *
 * private clearLeavingSlotState() {
 *   const queued = this.dayQueue.complete();
 *   if (queued) void this.onCalendarDateSelected(queued);
 * }
 * ```
 *
 * 2) When completion happens inline at the end of the handler:
 * ```
 * async gotoDateAsync(date: DateTime) {
 *   if (!this.dayQueue.tryStart(date)) return;
 *   let queued: DateTime | null = null;
 *   try {
 *     // ...async work, may early-return on viewDestroyed...
 *   } finally {
 *     queued = this.dayQueue.complete();
 *   }
 *   // guard re-dispatch — a destroyed view must not be re-entered.
 *   if (queued && !this.viewDestroyed) void this.gotoDateAsync(queued);
 * }
 * ```
 */
export class DayAnimationQueue {
  private _isAnimating = false;
  private _queuedDate: DateTime | null = null;

  /**
   * Try to start an animation for the given date.
   * If animation is already in progress, queues this date for later.
   * @returns true if animation can proceed, false if queued
   */
  tryStart(date: DateTime): boolean {
    if (this._isAnimating) {
      this._queuedDate = date;
      return false;
    }
    this._isAnimating = true;
    return true;
  }

  /**
   * Called when animation completes.
   * @returns the queued date if any, or null
   */
  complete(): DateTime | null {
    const queued = this._queuedDate;
    this._queuedDate = null;
    this._isAnimating = false;
    return queued;
  }

  /** Check if currently animating */
  get isAnimating(): boolean {
    return this._isAnimating;
  }
}
